
import React from "react";
import { Filter, Briefcase, Code, Palette, LineChart, Star, Settings, RefreshCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

interface CollaborationFiltersProps {
  currentCategory: string;
  onCategoryChange: (category: string) => void;
}

export const CollaborationFilters = ({ 
  currentCategory, 
  onCategoryChange 
}: CollaborationFiltersProps) => {
  const categories = [
    { id: "all", name: "All Projects", icon: Briefcase },
    { id: "web", name: "Web Development", icon: Code },
    { id: "mobile", name: "Mobile Apps", icon: Code },
    { id: "design", name: "Design", icon: Palette },
    { id: "marketing", name: "Marketing", icon: LineChart },
    { id: "content", name: "Content Creation", icon: Palette },
    { id: "other", name: "Other", icon: Settings }
  ];

  return (
    <Card className="h-fit sticky top-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Filter className="h-5 w-5 text-indigo-500" />
          Filters
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Project Categories</Label>
          <div className="space-y-1.5">
            {categories.map((category) => {
              const CategoryIcon = category.icon;
              return (
                <Button
                  key={category.id}
                  variant="ghost"
                  className={`w-full justify-start text-sm font-normal ${currentCategory === category.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600'}`}
                  onClick={() => onCategoryChange(category.id)}
                >
                  <CategoryIcon className="h-4 w-4 mr-2" />
                  {category.name}
                </Button>
              );
            })}
          </div>
        </div>
        
        <Separator />
        
        <div className="space-y-3">
          <Label className="text-sm font-medium">Budget Range</Label>
          <Slider defaultValue={[0, 100]} max={100} step={1} />
          <div className="flex justify-between text-xs text-gray-500">
            <span>$0</span>
            <span>$10,000+</span>
          </div>
        </div>
        
        <Separator />
        
        <div className="space-y-3">
          <Label className="text-sm font-medium">Team Size</Label>
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" size="sm" className="text-xs">Small (2-3)</Button>
            <Button variant="outline" size="sm" className="text-xs">Medium (4-5)</Button>
            <Button variant="outline" size="sm" className="text-xs">Large (6+)</Button>
          </div>
        </div>
        
        <Separator />
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium" htmlFor="remote-only">Remote Only</Label>
            <Switch id="remote-only" />
          </div>
          
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium" htmlFor="verified-owners">Verified Owners</Label>
            <Switch id="verified-owners" />
          </div>
        </div>
        
        <Button 
          variant="outline" 
          className="w-full text-indigo-600 border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50"
          size="sm"
        >
          <RefreshCcw className="h-3.5 w-3.5 mr-2" />
          Reset Filters
        </Button>
      </CardContent>
    </Card>
  );
};
